import { useState, useEffect, useCallback } from 'react';
import { SlideModal } from '@podus/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import type { Case } from '@/types';
import { STATUS_BADGE } from './components/shared';
import { AssignMediatorModal } from './components/AssignMediatorModal';
import { AcceptCaseModal } from './components/AcceptCaseModal';
import { RejectCaseModal } from './components/RejectCaseModal';
import { UpdateStatusModal } from './components/UpdateStatusModal';
import { ScheduleMeetingModal } from './components/ScheduleMeetingModal';
import { CaseDetailsTab } from './components/CaseDetailsTab';
import { FinancialsTab } from './components/FinancialsTab';
import { ScheduleTab } from './components/ScheduleTab';
import { CaseTimelineTab } from './components/CaseTimelineTab';

/* ─── Types ─── */

export type CaseStatus = 'Draft' | 'New' | 'Assigned' | 'In Progress' | 'Resolved' | 'Reopen';
type TabId = 'details' | 'schedule' | 'timeline' | 'financials';

export interface CaseRow {
    id: number;
    sn: number;
    caseNumber?: string;
    date: string;
    complainantName: string;
    caseCategory: string;
    disputeCategory: string;
    evidence?: number | string;
    amountPaid: string;
    status: CaseStatus;
}

export interface CaseViewModalProps {
    open: boolean;
    onClose: () => void;
    caseData: CaseRow | null;
    /** Called after a successful action (assign, accept, reject, update status) */
    onCaseUpdated?: () => void;
}

const MODAL_TABS: { id: TabId; label: string }[] = [
    { id: 'details', label: 'Case Details' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'timeline', label: 'Case Timeline' },
    { id: 'financials', label: 'Financials' },
];

/* ─── Main Modal ─── */

export default function CaseViewModal({ open, onClose, caseData, onCaseUpdated }: CaseViewModalProps) {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<TabId>('details');
    const [fullCase, setFullCase] = useState<Case | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [acceptOpen, setAcceptOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const fetchFullCase = useCallback(async () => {
        if (!caseData?.id) return;
        try {
            const res = await api.get<Case>(`/api/cases/${caseData.id}`);
            setFullCase(res.response.data ?? null);
        } catch {
            setFullCase(null);
        }
    }, [caseData?.id]);

    useEffect(() => {
        if (!open || !caseData?.id) return;
        setFullCase(null);
        void fetchFullCase();
    }, [open, caseData?.id, fetchFullCase]);

    const [actionCompleted, setActionCompleted] = useState(false);

    const handleActionSuccess = useCallback(() => {
        void fetchFullCase();
        setActionCompleted(true);
        onCaseUpdated?.();
    }, [fetchFullCase, onCaseUpdated]);

    const closeOuterIfCompleted = useCallback(() => {
        if (actionCompleted) {
            setActionCompleted(false);
            setActiveTab('details');
            setFullCase(null);
            onClose();
        }
    }, [actionCompleted, onClose]);

    if (!caseData) return null;

    const role = user?.role;
    const isStaffAdmin = role === 'Admin' || role === 'Case Manager';
    const isMediator = role === 'Mediator';
    const status = caseData.status;

    let footer: React.ReactNode = null;
    if (isStaffAdmin && activeTab === 'schedule') {
        footer = (
            <button
                type="button"
                onClick={() => setScheduleOpen(true)}
                className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400"
            >
                Create Schedule
            </button>
        );
    } else if (isStaffAdmin) {
        if (status === 'New') {
            footer = (
                <button
                    type="button"
                    onClick={() => setAssignOpen(true)}
                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400"
                >
                    Assign To Mediator
                </button>
            );
        } else if (status === 'Assigned' || status === 'In Progress' || status === 'Reopen') {
            footer = (
                <button
                    type="button"
                    onClick={() => setUpdateOpen(true)}
                    className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[13px] font-bold text-white transition-all hover:bg-secondary-400"
                >
                    Update
                </button>
            );
        }
    } else if (isMediator && status === 'Assigned') {
        footer = (
            <div className="flex items-center gap-4">
                <button
                    type="button"
                    onClick={() => setRejectOpen(true)}
                    className="flex flex-1 items-center justify-center rounded-xl bg-red-500 py-4 text-[13px] font-bold text-white transition-colors hover:bg-red-600"
                >
                    Reject
                </button>
                <button
                    type="button"
                    onClick={() => setAcceptOpen(true)}
                    className="flex flex-1 items-center justify-center rounded-xl bg-green-600 py-4 text-[13px] font-bold text-white transition-colors hover:bg-green-700"
                >
                    Accept Case
                </button>
            </div>
        );
    }

    const statusBadge = (
        <span className={`rounded-full border px-4 py-1 text-[11px] font-medium ${STATUS_BADGE[status]}`}>
            {status}
        </span>
    );

    return (
        <>
            <SlideModal
                open={open}
                onClose={() => {
                    setActiveTab('details');
                    setFullCase(null);
                    onClose();
                }}
                title={<h2 className="text-lg font-bold text-secondary">{caseData.caseCategory} Case</h2>}
                headerRight={statusBadge}
                maxWidth="max-w-3xl"
            >
                <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
                    <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-[#eee] px-4 pt-2 lg:flex-col lg:gap-0 lg:overflow-x-visible lg:border-b-0 lg:border-r lg:pt-6">
                        {MODAL_TABS.filter((tab) => !(isMediator && tab.id === 'financials')).map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`whitespace-nowrap px-3 py-2 text-[14px] font-medium transition-colors lg:w-full lg:text-left ${
                                    activeTab === tab.id ? 'text-primary' : 'text-[#555] hover:text-secondary'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex-1 overflow-y-auto p-4 lg:p-6">
                        {activeTab === 'details' && <CaseDetailsTab caseRow={caseData} fullCase={fullCase} />}
                        {activeTab === 'schedule' && <ScheduleTab schedules={fullCase?.schedules ?? []} />}
                        {activeTab === 'timeline' && <CaseTimelineTab timeline={fullCase?.timeline ?? []} />}
                        {activeTab === 'financials' && !isMediator && (
                            <FinancialsTab transactions={fullCase?.transactions ?? []} />
                        )}
                    </div>
                </div>

                {footer && <div className="shrink-0 px-6 pb-6 pt-4">{footer}</div>}
            </SlideModal>

            <AssignMediatorModal
                open={assignOpen}
                caseId={caseData.id}
                onClose={() => {
                    setAssignOpen(false);
                    closeOuterIfCompleted();
                }}
                onAssigned={handleActionSuccess}
            />
            <AcceptCaseModal
                open={acceptOpen}
                caseId={caseData.id}
                onClose={() => {
                    setAcceptOpen(false);
                    closeOuterIfCompleted();
                }}
                onAccepted={handleActionSuccess}
            />
            <RejectCaseModal
                open={rejectOpen}
                caseId={caseData.id}
                onClose={() => {
                    setRejectOpen(false);
                    closeOuterIfCompleted();
                }}
                onRejected={handleActionSuccess}
            />
            <UpdateStatusModal
                open={updateOpen}
                caseId={caseData.id}
                currentStatus={status}
                onClose={() => {
                    setUpdateOpen(false);
                    closeOuterIfCompleted();
                }}
                onUpdated={handleActionSuccess}
            />
            <ScheduleMeetingModal
                open={scheduleOpen}
                caseId={caseData.id}
                onClose={() => {
                    setScheduleOpen(false);
                    closeOuterIfCompleted();
                }}
                onCreated={handleActionSuccess}
            />
        </>
    );
}
